import { BowlingSummaryDay, Workout } from '@strength/domain-workout';

export type ResolvedSlotWorkout = {
  /** Prescription-only workout (block + athlete, no actual). Used by PrescriptionSlot. */
  workout: Workout;
  /**
   * Workout merged with the actual layer. Only set for cross-block overlays
   * that have an actual layer. ActualSlot uses this when present so it renders
   * the recorded values rather than the prescription values.
   */
  actualWorkout?: Workout;
  /**
   * True when a cross-block overlay is rendering for this slot. Slots use
   * this to switch into the dataSource-disabled rendering branch — the cell
   * is non-editable because the source workout owns its value.
   */
  isOverlay: boolean;
  /**
   * Unified "this slot has actuals to display" flag — sourced from the
   * cross-block row when an overlay is in effect, or from the caller's
   * `currentHasActual` for the current block. Lets `ActualSlot` apply one
   * gating check regardless of which workout is rendering.
   */
  hasActual: boolean;
  /**
   * Name of the team the rendering workout belongs to, only set when that
   * team is cross-team (differs from the viewing team). Hovers use it for a
   * "Team Entered" line.
   */
  crossTeamName?: string;
};

/**
 * Decides which Workout to render in a block-summary slot, whether a
 * cross-block overlay is in effect, and whether the rendering workout has
 * actuals to display. Pass `currentHasActual` from the current block's
 * `ActualWorkout.hasActual` (or omit for slots that don't care, like
 * PrescriptionSlot) so the no-overlay branch carries the right flag.
 */
export function resolveSlotWorkout(
  current: Workout,
  crossBlock: BowlingSummaryDay | undefined,
  currentHasActual = false,
): ResolvedSlotWorkout {
  if (!crossBlock || crossBlock.kind === 'current') {
    return { workout: current, isOverlay: false, hasActual: currentHasActual };
  }
  if (crossBlock.kind === 'duplicate') {
    return {
      workout: crossBlock.primary.workout,
      actualWorkout: crossBlock.primary.actualWorkout,
      isOverlay: true,
      hasActual: crossBlock.primary.hasActual,
      crossTeamName: crossBlock.primary.isCrossTeam
        ? crossBlock.primary.teamName
        : undefined,
    };
  }
  return {
    workout: crossBlock.workout,
    actualWorkout: crossBlock.actualWorkout,
    isOverlay: true,
    hasActual: crossBlock.hasActual,
    crossTeamName: crossBlock.isCrossTeam
      ? crossBlock.source.teamName
      : undefined,
  };
}
